import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

// FastAPI returns validation errors (422) as `detail: [{loc, msg, type, ...}]`.
// Passing that array straight into a toast rendered an object as a React child
// (React error #31) and crashed the whole app into a black screen. Every error
// response therefore gets a plain, German string in `detail`.
const FIELD_LABELS = {
  email: "E-Mail",
  customer_email: "E-Mail",
  customer_name: "Name",
  company_name: "Firma",
  contact_name: "Ansprechpartner",
  phone: "Telefon",
  billing_street: "Straße",
  billing_zip: "PLZ",
  billing_city: "Ort",
  billing_country: "Land",
  vat_id: "USt-IdNr",
  slot_ids: "Tische",
  items: "Tickets",
  quantity: "Menge",
  password: "Passwort",
};

const humanizeValidationItem = (item) => {
  const loc = Array.isArray(item?.loc) ? item.loc.filter((p) => p !== "body") : [];
  const field = loc.length ? String(loc[loc.length - 1]) : "";
  const label = FIELD_LABELS[field] || field || "Eingabe";
  const msg = String(item?.msg || "");
  if (String(item?.type || "").includes("email") || /email address/i.test(msg)) {
    return `${label}: bitte eine gültige E-Mail-Adresse eingeben`;
  }
  if (item?.type === "missing") return `${label}: Pflichtfeld fehlt`;
  return `${label}: ${msg || "ungültige Eingabe"}`;
};

export const formatApiDetail = (detail) => {
  if (!detail) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(humanizeValidationItem).join(" · ");
  if (typeof detail === "object") return detail.msg || detail.message || JSON.stringify(detail);
  return String(detail);
};

api.interceptors.response.use(
  (res) => res,
  (error) => {
    const data = error?.response?.data;
    if (data && typeof data === "object" && data.detail && typeof data.detail !== "string") {
      data.detail = formatApiDetail(data.detail);
    }
    return Promise.reject(error);
  }
);

export const authHeaders = () => {
  const token = localStorage.getItem("ccc_admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Raw admin token (for query-param authenticated downloads via window.open).
export const getAdminToken = () => localStorage.getItem("ccc_admin_token") || "";

// --- Exhibitor (Aussteller) auth ---
export const EXHIBITOR_TOKEN_KEY = "ccc_exhibitor_token";

export const getExhibitorToken = () => localStorage.getItem(EXHIBITOR_TOKEN_KEY) || "";
export const setExhibitorToken = (t) => {
  if (t) localStorage.setItem(EXHIBITOR_TOKEN_KEY, t);
  else localStorage.removeItem(EXHIBITOR_TOKEN_KEY);
};
export const exhibitorHeaders = () => {
  const t = getExhibitorToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Resolve a possibly relative URL (e.g. /api/uploads/...) to an absolute URL
export const absoluteUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${BACKEND_URL}${path.startsWith("/") ? "" : "/"}${path}`;
};

// Normalize a user-provided external link (e.g. exhibitor website) so it always
// has a protocol. Without this, "www.tcg-siegburg.de" is treated as a RELATIVE
// URL by the browser and resolves to "cologne-card-con.de/www.tcg-siegburg.de".
export const externalUrl = (url) => {
  if (!url) return "";
  const trimmed = String(url).trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^(mailto:|tel:)/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return `https://${trimmed.replace(/^\/+/, "")}`;
};
